import { useId } from 'react';

/**
 * One labelled control. The parts that matter for accessibility, and that are
 * easy to get wrong when each field is written by hand:
 *
 *   - the label is a real <label htmlFor>, not a floating placeholder, so it
 *     stays visible once there is text in the box and is announced on focus;
 *   - the error is tied to the input with aria-describedby and marked
 *     aria-invalid, so a screen reader reads the reason rather than just
 *     "invalid entry";
 *   - required fields are marked in the accessible name too, not only with a
 *     red asterisk that a screen reader skips.
 */
export default function Field({
  label,
  error,
  hint,
  required = false,
  as: Control = 'input',
  className = '',
  children,
  ...props
}) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [error && errorId, hint && hintId].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <label htmlFor={id} className="block text-[15px] font-bold text-forest">
        {label}
        {required && (
          <span className="text-coral" aria-hidden="true">
            {' '}
            *
          </span>
        )}
        {!required && <span className="font-normal text-ink-soft"> (optional)</span>}
      </label>

      {hint && (
        <p id={hintId} className="mt-1 text-[13.5px] text-ink-soft">
          {hint}
        </p>
      )}

      <Control
        id={id}
        required={required}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy || undefined}
        className={[
          'mt-1.5 w-full rounded-2xl border-2 bg-white px-4 py-3 text-[16px] text-ink',
          'transition-colors placeholder:text-ink-soft/60',
          error ? 'border-coral' : 'border-sage focus:border-forest',
        ].join(' ')}
        {...props}
      >
        {children}
      </Control>

      {error && (
        <p id={errorId} className="mt-1.5 text-[14px] font-bold text-coral">
          {error}
        </p>
      )}
    </div>
  );
}
